// packages/web/src/components/Matches/UpcomingMatchesGallery.tsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Chip,
  CircularProgress,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import { format } from 'date-fns';
import { Challenge, formatPlayerRatings } from '@matchpoint/shared';

const GallerySection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: '#fafafa',
  borderRadius: '12px',
  border: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const GalleryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.125rem',
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    gap: theme.spacing(0.5),
  },
}));

const ManageButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '0.875rem',
  fontWeight: 600,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '6px',
  border: '2px solid #1a1a1a',
  textTransform: 'none',
  minHeight: 32,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    padding: theme.spacing(0.4, 1.25),
    minHeight: 28,
  },
}));

const MatchesScroller = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  overflowX: 'auto',
  paddingBottom: theme.spacing(0.5),
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c4d600',
    borderRadius: '2px',
    '&:hover': {
      backgroundColor: '#b3c200',
    },
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

const MatchCard = styled(Card)<{ $status?: string }>(({ theme, $status }) => ({
  minWidth: '180px',
  maxWidth: '180px',
  borderRadius: '8px',
  border: $status === 'accepted' 
    ? '2px solid #c4d600' 
    : $status === 'pending' 
    ? '2px solid #ff9800' 
    : '2px solid #e0e0e0',
  backgroundColor: $status === 'accepted'
    ? '#f9fdf0'
    : $status === 'pending'
    ? '#fff8f0'
    : '#ffffff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  height: 20,
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const PlayerAvatar = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #84b817 0%, #c4d600 100%)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.875rem',
  boxShadow: '0 2px 6px rgba(132, 184, 23, 0.25)',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '0.8rem',
  fontWeight: 600,
  borderRadius: '6px',
  padding: theme.spacing(0.6, 1.5),
  minHeight: 32,
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.25),
    minHeight: 28,
  },
}));

const AcceptButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  boxShadow: '0 2px 6px rgba(196, 214, 0, 0.2)',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 4px 10px rgba(196, 214, 0, 0.3)',
    transform: 'translateY(-1px)',
  },
}));

const DeclineButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  border: '1px solid #e0e0e0',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
}));

const ViewDetailsButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#84b817',
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(0.75, 0),
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: 'rgba(132, 184, 23, 0.08)',
    color: '#679614',
  },
  transition: 'all 0.2s ease',
}));

const EmptyState = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 3),
  textAlign: 'center',
  backgroundColor: '#ffffff',
  border: '2px dashed #e5e5e5',
  borderRadius: '8px',
  boxShadow: 'none',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2),
  },
}));

const EmptyStateTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1a1a1a',
  marginBottom: theme.spacing(0.75),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
  },
}));

const EmptyStateSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#666',
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

interface UpcomingMatchesGalleryProps {
  matches: Challenge[];
  loading: boolean;
  currentUserId?: string;
  onAcceptMatch: (challengeId: string) => Promise<void>;
  onDeclineMatch: (challengeId: string) => Promise<void>;
  onManageSchedule: () => void;
  onViewDetails: (challenge: Challenge) => void;
}

export const UpcomingMatchesGallery: React.FC<UpcomingMatchesGalleryProps> = ({
  matches,
  loading,
  currentUserId,
  onAcceptMatch,
  onDeclineMatch,
  onManageSchedule,
  onViewDetails
}) => {
  if (!currentUserId) {
    return null;
  }

  return (
    <GallerySection>
      <GalleryHeader>
        <HeaderTitle>
          <EventIcon sx={{ color: '#84b817', fontSize: '1.375rem' }} />
          Your Upcoming Matches
        </HeaderTitle>
        {matches.length > 0 && (
          <ManageButton onClick={onManageSchedule}>
            Manage Schedule
          </ManageButton>
        )}
      </GalleryHeader>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={40} sx={{ color: '#c4d600' }} />
        </Box>
      ) : matches.length > 0 ? (
        <MatchesScroller>
          {matches.map((match) => {
            const isReceived = match.toUserId === currentUserId;
            const opponentName = isReceived ? match.fromUserName : match.toUserName;
            const opponentRatings = isReceived ? match.fromPlayerRatings : match.toPlayerRatings;
            const matchDate = match.startTime;
            const matchEndTime = match.endTime;
            
            return (
              <MatchCard key={match.id} $status={match.status}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                      {format(matchDate, 'EEE').toUpperCase()}
                    </Typography>
                    <StatusChip
                      label={match.status === 'accepted' ? 'CONFIRMED' : 'PENDING'}
                      color={match.status === 'accepted' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, fontSize: '0.9rem', color: '#1a1a1a' }}>
                    {format(matchDate, 'h:mm a')} - {format(matchEndTime, 'h:mm a')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PlayerAvatar>
                      {opponentName.charAt(0).toUpperCase()}
                    </PlayerAvatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1a1a1a' }}>
                        {opponentName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {opponentRatings 
                          ? formatPlayerRatings(opponentRatings)
                          : 'Not Rated'
                        }
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: '#84b817' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {match.courtName}
                    </Typography>
                  </Box>
                  
                  {match.status === 'pending' && isReceived && (
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5 }}>
                      <AcceptButton
                        fullWidth
                        onClick={() => onAcceptMatch(match.id)}
                      >
                        Accept
                      </AcceptButton>
                      <DeclineButton
                        fullWidth
                        onClick={() => onDeclineMatch(match.id)}
                      >
                        Decline
                      </DeclineButton>
                    </Box>
                  )}
                  
                  {match.status === 'pending' && !isReceived && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.65rem', 
                        fontWeight: 700,
                        display: 'block',
                        mt: 0.75,
                        color: '#ff9800',
                        letterSpacing: '0.05em'
                      }}
                    >
                      YOUR REQUEST
                    </Typography>
                  )}
                  
                  {match.status === 'accepted' && (
                    <ViewDetailsButton
                      fullWidth
                      onClick={() => onViewDetails(match)}
                    >
                      View Details →
                    </ViewDetailsButton>
                  )}
                </CardContent>
              </MatchCard>
            );
          })}
        </MatchesScroller>
      ) : (
        <EmptyState>
          <EmptyStateTitle>
            No upcoming matches this week
          </EmptyStateTitle>
          <EmptyStateSubtitle>
            Challenge other players or publish your schedule to get started
          </EmptyStateSubtitle>
        </EmptyState>
      )}
    </GallerySection>
  );
};